// Shared phone formatter: turns the canonical "+CC NNNNNNNN" stored in
// UnifiedContact into a country-specific masked display ("+34 686 998
// 812", "+39 339 889 1310", etc.). One source of truth for the whole UI
// — import this util everywhere instead of pasting `tel:` links by hand.
//
// Storage format on the backend is already E.164-with-space ("+CC
// NNNNN"), so parsing is straightforward; we still tolerate raw user
// input (no prefix, dashes, parens) and fall back to the original
// string when libphonenumber can't make sense of it.

import { parsePhoneNumberFromString } from "libphonenumber-js";

import type { CountryCode } from "libphonenumber-js";

/** Default region used when the input has no `+` prefix. Most of the
 *  data is Spanish; callers that know better (e.g. country of the
 *  producer) can override per call. */
export const DEFAULT_PHONE_REGION: CountryCode = "ES";

/** Países donde la plataforma tiene productores. Cuando un teléfono
 *  no trae prefijo y el `region` por defecto no lo valida (ej. un
 *  móvil italiano `348 580 0289` parseado como ES), recorremos esta
 *  lista y nos quedamos con el primer país que lo da por válido.
 *  Mantén aquí solo los países que realmente usáis para evitar
 *  falsos positivos. */
const FALLBACK_REGIONS: CountryCode[] = [
  "IT", "PT", "FR", "BR", "MX", "PE", "CL", "UY", "ZA", "US", "GB",
];

/**
 * Format a phone for display.
 *
 *   formatPhone("+34686998812")              → "+34 686 99 88 12"
 *   formatPhone("+39 339 8891310")           → "+39 339 889 1310"
 *   formatPhone("686998812")                 → "+34 686 99 88 12"
 *   formatPhone("348 580 0289")              → "+39 348 580 0289"   (auto IT)
 *   formatPhone("")                          → ""
 *   formatPhone("ext 234 algo raro")         → "ext 234 algo raro"
 */
export const formatPhone = (
  raw: string | null | undefined,
  region: CountryCode = DEFAULT_PHONE_REGION,
): string => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";

  try {
    // 1) Try the explicit region (or the default ES).
    const primary = parsePhoneNumberFromString(trimmed, region);
    if (primary && primary.isValid()) {
      return primary.formatInternational();
    }
    // 2) Only fall back when the input has no `+` prefix. If it
    // already carries an explicit country code we trust it (a
    // malformed +XX shouldn't be silently rewritten as another
    // country's number).
    if (!trimmed.startsWith("+")) {
      for (const r of FALLBACK_REGIONS) {
        if (r === region) continue;
        const candidate = parsePhoneNumberFromString(trimmed, r);
        if (candidate && candidate.isValid()) {
          return candidate.formatInternational();
        }
      }
    }
  } catch {
    // libphonenumber-js doesn't normally throw, but guard anyway.
  }
  return trimmed;
};

/**
 * Strip everything except digits to build a `tel:` href. Always
 * keeps the leading `+`. Empty input → empty string.
 *
 *   phoneToTelHref("+34 686 998 812") → "+34686998812"
 *   phoneToTelHref("686-998-812")     → "686998812"
 */
export const phoneToTelHref = (raw: string | null | undefined): string => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  const plus = trimmed.startsWith("+") ? "+" : "";
  const digits = trimmed.replace(/[^\d]/g, "");
  return plus + digits;
};
